/**
 * Geography Service
 * HTTP operations for geographic data (regions, communes)
 */

import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import type { LC_GetRegionsOut, LC_GetCommunesByRegionOut, Region, Commune } from './geography.types';
import { mapRegions, mapCommunes } from './geography.mapper';

/**
 * Service class for geography operations
 */
export class GeographyService {
  /**
   * Get regions by country
   * GET /v{version}/geography/{country}/regions
   */
  async getRegions(country: string): Promise<ApiResponse<Region[]>> {
    const endpoint = API_CONFIG.ENDPOINTS.GEOGRAPHY.REGIONS(country);

    const response = await apiClient.get<LC_GetRegionsOut>(endpoint);

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    const regions = mapRegions(response.data);
    return { ...response, data: regions };
  }

  /**
   * Get communes by region
   * GET /v{version}/geography/{country}/regions/{regionId}/communes
   * @param country - Country code (e.g., 'CL')
   * @param regionId - Region ID
   * @param forceReload - If true, forces reload from DB (admin only)
   */
  async getCommunes(
    country: string,
    regionId: number,
    forceReload?: boolean,
  ): Promise<ApiResponse<Commune[]>> {
    let endpoint = API_CONFIG.ENDPOINTS.GEOGRAPHY.COMMUNES(country, regionId);
    
    if (forceReload) {
      endpoint += '?forceReload=true';
    }

    const response = await apiClient.get<LC_GetCommunesByRegionOut>(endpoint);

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    const communes = mapCommunes(response.data);
    return { ...response, data: communes };
  }
}

export const geographyService = new GeographyService();
